import { useQuery } from "@apollo/client";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  SxProps,
} from "@mui/material";
import { useContext, useRef } from "react";
import { Link } from "react-router-dom";
import { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Autoplay, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { gql } from "../../../__generated__";
import {
  Currency,
  Iso639,
  ProductOrderField,
} from "../../../__generated__/graphql";
import GlobalContext from "../../../global-context";
import { getCloudinaryUrl } from "../../../helpers/cloudinary";

export default function NewTours() {
  const swiperRef = useRef<SwiperType>();
  const globalContext = useContext(GlobalContext);
  const { data, loading, error } = useQuery(GET_NEW_PRODUCTS, {
    variables: {
      input: {
        isNew: true,
        countryId: getUserCountry(),
        orderField: ProductOrderField.LastPublished,
      },
      lang: Iso639.En,
      currency: globalContext.currency as Currency,
    },
  });

  const products = data?.products.records || [];
  if (!loading && products.length === 0) return <></>; // don't show section if no new products

  return (
    <>
      <Box sx={{ fontSize: "18px", fontWeight: "bold", marginBottom: "20px" }}>
        New Tours
      </Box>
      {error && <>ERROR</>}
      {loading && <LinearProgress color="error" />}
      {!loading && (
        <Box
          sx={{
            position: "relative",
            width: "100%",
          }}
        >
          <Swiper
            modules={[Navigation, Autoplay]}
            spaceBetween={20}
            slidesPerView={3}
            breakpoints={{
              10: { slidesPerView: 1 },
              600: { slidesPerView: 1 },
              900: { slidesPerView: 2 },
              1200: { slidesPerView: 3 },
            }}
            loop={true}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            onBeforeInit={(swiper) => {
              swiperRef.current = swiper;
            }}
          >
            {products.map((product) => (
              <SwiperSlide key={product.id}>
                <Box position="relative">
                  <Link to={`/tours/${product.id}`}>
                    {product.mainImage && (
                      <Paper
                        elevation={5}
                        sx={{
                          height: 250,
                          backgroundImage: `url(${getCloudinaryUrl(
                            "image",
                            product.mainImage,
                            { height: 500 }
                          )})`,
                          backgroundSize: "cover",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "center",
                          position: "relative",
                          overflow: "hidden",
                          borderRadius: "10px",
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        width: "100%",
                        background: "rgba(0, 0, 0, 0.6)",
                        color: "white",
                        textAlign: "center",
                        padding: "8px",
                        boxSizing: "border-box",
                        fontSize: "14px",
                        fontWeight: "bold",
                        borderRadius: "0px 0px 10px 10px",
                      }}
                    >
                      {product.translation?.name}
                      <Stack
                        sx={{ mt: "5px", fontSize: "10px" }}
                        direction="row"
                        justifyContent="space-between"
                      >
                        <span>
                          {product.location.translation?.name},{" "}
                          {product.location.country.translation?.name}
                        </span>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          <AccessTimeIcon sx={{ fontSize: "12px" }} />
                          <span>{product.duration / 60} hours</span>
                        </Stack>
                      </Stack>
                    </Box>
                  </Link>
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>

          <Arrow
            icon={<ChevronLeftIcon sx={{ fontSize: 30 }} />}
            onClick={() => swiperRef.current?.slidePrev()}
            sx={{ left: 5 }}
          />
          <Arrow
            icon={<ChevronRightIcon sx={{ fontSize: 30 }} />}
            onClick={() => swiperRef.current?.slideNext()}
            sx={{ right: 5 }}
          />
        </Box>
      )}
    </>
  );

  function Arrow(props: {
    icon: JSX.Element;
    onClick: React.MouseEventHandler<HTMLButtonElement>;
    sx?: SxProps;
  }) {
    return (
      <IconButton
        onClick={props.onClick}
        sx={{
          ...props.sx,
          position: "absolute",
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 10,
          color: "red",
          backgroundColor: "white",
          "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.8)" },
        }}
      >
        {props.icon}
      </IconButton>
    );
  }

  function getUserCountry() {
    const localStorageUser = JSON.parse(localStorage.getItem("user") || "");
    return localStorageUser ? localStorageUser.countryId : null;
  }
}

const GET_NEW_PRODUCTS = gql(`
  query GetNewProducts(
    $input: ProductsInput
    $lang: Iso639!
    $currency: Currency!
  ) {
    products(input: $input) {
      totalRecords
      records {
        id
        duration
        mainImage
        translation(lang: $lang) {
          name
          metaDescription
        }
        location {
          translation(lang: $lang) {
            name
          }
          country {
            translation(lang: $lang) {
              name
            }
          }
        }
        mainPrice(currency: $currency) {
          amount
          currency
          isGroup
        }
      }
    }
  }
`);
